import { useEffect, useState, useCallback, useRef } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_URL as string;

// Title the backend stamps on the notification raised for every broadcast
// recipient (see backend chat.dto.ts BROADCAST_NOTIFICATION_TITLE). Compared
// normalized (trim + lowercase) so stray whitespace/casing can't defeat it.
export const BROADCAST_NOTIFICATION_TITLE =
  "Message from JamboCare Leadership Team";

const useSocketNotifications = (userId: string) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasNew, setHasNew] = useState(false);
  // Newest unread broadcast, surfaced as a pop-up on whichever screen the user
  // is on. Only one is shown at a time; dismissing marks it read, so the next
  // poll brings up the next one (getAllNotifiction returns unread only).
  const [broadcastNotification, setBroadcastNotification] = useState<any>(null);
  // Ids of broadcasts already dismissed this session. Marking a notification
  // read on the server is async, so a poll can still return it as unread for a
  // cycle or two after dismissal — without this guard the modal would re-open.
  const dismissedBroadcastIdsRef = useRef<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const isFirstLoad = useRef(true); // Prevent sound on first load

  useEffect(() => {
    if (!userId) return;

    // Initialize socket connection once
    socketRef.current = io(SOCKET_SERVER_URL, {
      query: { receiverId: userId },
    });

    socketRef.current.on("connect", () => {
      console.log("Socket connected");
    });

    socketRef.current.on("disconnect", (reason: any) => {
      console.log("Socket disconnected:", reason);
    });

    const handleNotifications = (data: any) => {
      if (data?.notification?.length) {
        setNotifications((prev) => {
          const updatedNotifs = data.notification.map((newNotif: any) => {
            const existingNotif = prev.find((n) => n.id === newNotif.id);
            return existingNotif ? { ...existingNotif, ...newNotif } : newNotif;
          });

          // Check if there's any new notification
          const hasNewNotifications = updatedNotifs.some(
            (notif: any) => !prev.some((n) => n.id === notif.id)
          );

          if (hasNewNotifications) {
            setHasNew(true);
            if (!isFirstLoad.current) {
              new Audio("/notification-sound.mp3")?.play();
            }
          }

          return updatedNotifs;
        });

        // Surface the newest unread broadcast as a pop-up. Kept separate from
        // the bell state above so existing notification behaviour is untouched.
        // If one is already showing, leave it — dismissing reveals the next.
        const newestBroadcast = data.notification
          .filter(
            (n: any) =>
              n?.title?.trim().toLowerCase() ===
                BROADCAST_NOTIFICATION_TITLE.toLowerCase() &&
              !dismissedBroadcastIdsRef.current.has(n?.id),
          )
          .sort(
            (a: any, b: any) =>
              new Date(b?.createdAt).getTime() -
              new Date(a?.createdAt).getTime()
          )[0];

        if (newestBroadcast) {
          setBroadcastNotification((prev: any) => prev ?? newestBroadcast);
        }
      }
    };

    // Listen for notifications
    socketRef.current.on(userId, handleNotifications);

    // Fetch notifications immediately
    socketRef.current.emit("getNotification", { receiverId: userId });

    // Fetch notifications every 5 seconds
    const interval = setInterval(() => {
      socketRef.current?.emit("getNotification", { receiverId: userId });
    }, 5000);

    // Mark first load as complete after initial fetch
    setTimeout(() => {
      isFirstLoad.current = false;
    }, 1000);

    return () => {
      clearInterval(interval);
      if (socketRef.current) {
        socketRef.current.off(userId, handleNotifications);
        socketRef.current.disconnect();
      }
    };
  }, [userId]);

  // Function to clear notifications and refresh them
  const clearNotifications = useCallback(() => {
    setNotifications([]); // Clear notifications
    setHasNew(false);

    // Fetch fresh notifications without creating a new socket instance
    socketRef.current?.emit("getNotification", { receiverId: userId });
  }, [userId]);

  // Acknowledge the broadcast: mark it read server-side so it stops coming back
  // on the 5s poll, and clear it locally so the pop-up closes immediately.
  const dismissBroadcast = useCallback(() => {
    const id = broadcastNotification?.id;
    setBroadcastNotification(null);
    if (id) {
      // Remember it so an in-flight/next poll can't re-open the modal before
      // the server has marked it read.
      dismissedBroadcastIdsRef.current.add(id);
      socketRef.current?.emit("readNotification", { id });
      setNotifications((prev) => prev.filter((n: any) => n?.id !== id));
    }
  }, [broadcastNotification]);

  return {
    notifications,
    hasNew,
    setHasNew,
    clearNotifications,
    broadcastNotification,
    dismissBroadcast,
  };
};

export default useSocketNotifications;
